// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Grid, Group, Loader, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type { Coding, Organization, Patient, Practitioner } from '@medplum/fhirtypes';
import { AsyncAutocomplete, useMedplum } from '@medplum/react';
import { IconCheck, IconUserPlus } from '@tabler/icons-react';
import { type JSX, useEffect, useState } from 'react';
import { FormCard } from '../../../components/FormCard';
import { useRole } from '../../../hooks/useRole';
import {
  getOrganizationAddressLine,
  getPractitionerNpi,
  getTelecomValue,
  type ReferringProviderFormValues,
  saveReferringProvider,
} from '../../../utils/patient-referring-provider';
import { hasPermission } from '../../../utils/permissions';

function buildInitialValues(
  practitioner: Practitioner | undefined,
  organization: Organization | undefined
): ReferringProviderFormValues {
  const name = practitioner?.name?.[0];
  return {
    title: name?.prefix?.[0] ?? '',
    firstName: name?.given?.[0] ?? '',
    lastName: name?.family ?? '',
    practiceName: organization?.name ?? '',
    practiceAddress: getOrganizationAddressLine(organization),
    npi: getPractitionerNpi(practitioner),
    email: getTelecomValue(organization, 'email') || getTelecomValue(practitioner, 'email'),
    phone: getTelecomValue(organization, 'phone'),
    fax: getTelecomValue(organization, 'fax'),
  };
}

export interface ReferringProviderSectionProps {
  patient: Patient;
  practitioner: Practitioner | undefined;
  organization: Organization | undefined;
  loading: boolean;
  onSaved: (patient: Patient, practitioner: Practitioner, organization: Organization) => void;
}

export function ReferringProviderSection({
  patient,
  practitioner,
  organization,
  loading,
  onSaved,
}: ReferringProviderSectionProps): JSX.Element {
  const medplum = useMedplum();
  const { role, isAdmin } = useRole();
  const canEdit = hasPermission({ role, permission: 'edit-patient-profile', isAdmin });

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | undefined>();

  const hasProvider = Boolean(practitioner || organization);
  const initialValues = buildInitialValues(practitioner, organization);
  const form = useForm<ReferringProviderFormValues>({
    initialValues,
    validate: {
      firstName: (v) => (v.trim() ? null : 'First name is required'),
      lastName: (v) => (v.trim() ? null : 'Last name is required'),
      practiceName: (v) => (v.trim() ? null : 'Practice name is required'),
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: resync only when practitioner/organization change
  useEffect(() => {
    if (!isEditing) {
      const next = buildInitialValues(practitioner, organization);
      form.setValues(next);
      form.resetDirty(next);
      setSelectedOrganization(undefined);
    }
  }, [practitioner, organization]);

  const handleCancel = (): void => {
    form.reset();
    setSelectedOrganization(undefined);
    setIsEditing(false);
  };

  const clearSelectedOrganization = (): void => {
    setSelectedOrganization(undefined);
  };

  const loadOrganizations = async (input: string, signal: AbortSignal): Promise<Organization[]> => {
    const searchTerm = input.trim();
    if (searchTerm.length < 2) {
      return [];
    }

    try {
      const organizations = await medplum.searchResources(
        'Organization',
        new URLSearchParams({
          name: searchTerm,
          _count: '10',
          _sort: 'name',
          _tag: 'https://polarissleep.com/org-role|auxiliary',
        }),
        { signal }
      );
      return organizations.filter((org) =>
        org.meta?.tag?.some(
          (tag: Coding) => tag.system === 'https://polarissleep.com/org-role' && tag.code === 'auxiliary'
        )
      );
    } catch (err) {
      if (!signal.aborted) {
        console.error('Referring organization search failed', err);
      }
      return [];
    }
  };

  const handleSave = form.onSubmit(async (values) => {
    setSaving(true);
    try {
      const result = await saveReferringProvider(
        medplum,
        patient,
        { practitioner, organization },
        values,
        selectedOrganization
      );
      onSaved(result.patient, result.practitioner, result.organization);
      setIsEditing(false);
      showNotification({ color: 'green', icon: <IconCheck size={16} />, message: 'Referring provider updated' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setSaving(false);
    }
  });

  if (loading) {
    return (
      <FormCard title="Referring Provider">
        <Stack align="center" py="md">
          <Loader size="sm" />
        </Stack>
      </FormCard>
    );
  }

  let action: JSX.Element | null = null;
  if (isEditing) {
    action = (
      <Group gap="xs">
        <Button size="xs" variant="default" onClick={handleCancel} disabled={saving}>
          Cancel
        </Button>
        <Button size="xs" onClick={() => handleSave()} loading={saving}>
          Save
        </Button>
      </Group>
    );
  } else if (canEdit && hasProvider) {
    action = (
      <Button size="xs" onClick={() => setIsEditing(true)}>
        Edit
      </Button>
    );
  }

  if (!hasProvider && !isEditing) {
    return (
      <FormCard title="Referring Provider">
        <Stack align="center" gap="md" py="lg">
          <Text c="dimmed">No referring provider on file.</Text>
          {canEdit && (
            <Button leftSection={<IconUserPlus size={16} />} onClick={() => setIsEditing(true)}>
              Add Referring Provider
            </Button>
          )}
        </Stack>
      </FormCard>
    );
  }

  return (
    <FormCard title="Referring Provider" isEditing={isEditing} action={action}>
      <Grid>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <TextInput label="Title" placeholder="Dr." readOnly={!isEditing} {...form.getInputProps('title')} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <TextInput label="First Name" readOnly={!isEditing} {...form.getInputProps('firstName')} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <TextInput label="Last Name" readOnly={!isEditing} {...form.getInputProps('lastName')} />
        </Grid.Col>
        <Grid.Col span={12}>
          {isEditing && (
            <AsyncAutocomplete
              aria-label="Existing Referring Provider"
              placeholder="Search existing referring providers…"
              loadOptions={loadOrganizations}
              toOption={(item) => {
                const org = item as Organization;
                const address = getOrganizationAddressLine(org);
                return {
                  value: org.id ?? '',
                  label: address ? `${org.name ?? 'Unnamed'} — ${address}` : (org.name ?? 'Unnamed'),
                  resource: org,
                };
              }}
              onChange={(items) => {
                const next = items[0] as Organization | undefined;
                setSelectedOrganization(next);
                if (next) {
                  form.setValues({
                    practiceName: next.name ?? '',
                    practiceAddress: getOrganizationAddressLine(next),
                    phone: getTelecomValue(next, 'phone'),
                    fax: getTelecomValue(next, 'fax'),
                    email: getTelecomValue(next, 'email') || getTelecomValue(practitioner, 'email'),
                  });
                }
              }}
              maxValues={1}
            />
          )}
          <TextInput
            label="Practice Name"
            readOnly={!isEditing}
            {...form.getInputProps('practiceName')}
            onChange={(event) => {
              clearSelectedOrganization();
              form.getInputProps('practiceName').onChange(event);
            }}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="Practice Address"
            readOnly={!isEditing}
            {...form.getInputProps('practiceAddress')}
            onChange={(event) => {
              clearSelectedOrganization();
              form.getInputProps('practiceAddress').onChange(event);
            }}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput label="NPI" placeholder="10-digit NPI" readOnly={!isEditing} {...form.getInputProps('npi')} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput label="Email" readOnly={!isEditing} {...form.getInputProps('email')} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="Phone"
            placeholder="(555) 555-5555 ext. 123"
            readOnly={!isEditing}
            {...form.getInputProps('phone')}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="Fax"
            placeholder="(555) 555-5555 ext. 123"
            readOnly={!isEditing}
            {...form.getInputProps('fax')}
          />
        </Grid.Col>
      </Grid>
    </FormCard>
  );
}
